import React, { useState, useEffect } from 'react';
import { X, Save, RefreshCw } from 'lucide-react';
import { brandPackagesApi } from '../../api/modules/brandPackages';
import type {
  BrandPackage,
  BrandPackageColors,
  BrandPackageFonts,
} from '../../api/modules/brandPackages';
import { AVAILABLE_FONTS, loadGoogleFont } from '../../utils/fontLoader';
import { BrandPackagePreview } from './BrandPackagePreview';
import {
  DEFAULT_PALETTE,
  deriveComponentColors,
  isGlobalPalette,
} from '../../utils/deriveComponentColors';
import type { GlobalPalette } from '../../utils/deriveComponentColors';

interface BrandPackageEditorProps {
  brandPackage: BrandPackage | null; // null for create mode
  onSave: (savedPackage: BrandPackage) => void;
  onCancel: () => void;
}

const DEFAULT_FONTS: BrandPackageFonts = {
  headingFont: 'Inter',
  bodyFont: 'Inter',
  baseSize: '16px',
};

// Color palette fields with labels and descriptions
// Note: 'secondary' is intentionally omitted - it's stored in the palette for future use
// but not currently exposed in the UI since it's not actively used in component rendering
const PALETTE_FIELDS = [
  {
    key: 'primary' as const,
    label: 'Primary Color',
    description: 'Main brand color for buttons, links, and accents',
  },
  {
    key: 'background' as const,
    label: 'Background',
    description: 'Main page and section backgrounds',
  },
  {
    key: 'text' as const,
    label: 'Text Color',
    description: 'Primary text and headings',
  },
  {
    key: 'accent' as const,
    label: 'Accent Color',
    description: 'Stars, highlights, and special elements',
  },
  {
    key: 'dark' as const,
    label: 'Dark Color',
    description: 'Navbar and footer backgrounds',
  },
];

// Preset palettes for quick selection
const PRESET_PALETTES: { name: string; palette: GlobalPalette }[] = [
  {
    name: 'Ocean Blue',
    palette: {
      primary: '#0ea5e9',
      secondary: '#6366f1',
      background: '#ffffff',
      text: '#0f172a',
      accent: '#f59e0b',
      dark: '#0f172a',
    },
  },
  {
    name: 'Forest Green',
    palette: {
      primary: '#22c55e',
      secondary: '#14b8a6',
      background: '#ffffff',
      text: '#1e293b',
      accent: '#eab308',
      dark: '#1e3a2f',
    },
  },
  {
    name: 'Sunset Orange',
    palette: {
      primary: '#f97316',
      secondary: '#ef4444',
      background: '#fffbeb',
      text: '#451a03',
      accent: '#fbbf24',
      dark: '#7c2d12',
    },
  },
  {
    name: 'Royal Purple',
    palette: {
      primary: '#8b5cf6',
      secondary: '#ec4899',
      background: '#ffffff',
      text: '#1e1b4b',
      accent: '#fbbf24',
      dark: '#1e1b4b',
    },
  },
  {
    name: 'Minimalist Gray',
    palette: {
      primary: '#3b82f6',
      secondary: '#64748b',
      background: '#f8fafc',
      text: '#1e293b',
      accent: '#f59e0b',
      dark: '#1e293b',
    },
  },
  {
    name: 'Dark Mode',
    palette: {
      primary: '#3b82f6',
      secondary: '#8b5cf6',
      background: '#0f172a',
      text: '#f1f5f9',
      accent: '#fbbf24',
      dark: '#020617',
    },
  },
];

export const BrandPackageEditor: React.FC<BrandPackageEditorProps> = ({
  brandPackage,
  onSave,
  onCancel,
}) => {
  const isEditing = !!brandPackage;

  const [name, setName] = useState(brandPackage?.name || '');
  const [description, setDescription] = useState(brandPackage?.description || '');

  // Extract palette from existing brand package or use defaults
  const getInitialPalette = (): GlobalPalette => {
    if (brandPackage?.colors) {
      // Check if we stored a _palette object
      const storedPalette = (brandPackage.colors as unknown as { _palette?: GlobalPalette })
        ._palette;
      if (storedPalette && isGlobalPalette(storedPalette)) {
        return storedPalette;
      }
      // Check if it's already a global palette at root level
      if (isGlobalPalette(brandPackage.colors)) {
        return brandPackage.colors;
      }
      // Try to extract palette from component colors (legacy support)
      return {
        primary: brandPackage.colors.hero?.buttonBg || DEFAULT_PALETTE.primary,
        secondary: brandPackage.colors.pricing?.priceColor || DEFAULT_PALETTE.secondary,
        background: brandPackage.colors.hero?.background || DEFAULT_PALETTE.background,
        text: brandPackage.colors.hero?.text || DEFAULT_PALETTE.text,
        accent: brandPackage.colors.testimonials?.starColor || DEFAULT_PALETTE.accent,
        dark: brandPackage.colors.navbar?.background || DEFAULT_PALETTE.dark,
      };
    }
    return DEFAULT_PALETTE;
  };

  const [palette, setPalette] = useState<GlobalPalette>(getInitialPalette);
  const [fonts, setFonts] = useState<BrandPackageFonts>(brandPackage?.fonts || DEFAULT_FONTS);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load fonts when they change for preview
  useEffect(() => {
    if (fonts.headingFont) loadGoogleFont(fonts.headingFont);
    if (fonts.bodyFont) loadGoogleFont(fonts.bodyFont);
  }, [fonts.headingFont, fonts.bodyFont]);

  const updatePaletteColor = (key: keyof GlobalPalette, value: string) => {
    setPalette(prev => ({ ...prev, [key]: value }));
  };

  const applyPreset = (presetPalette: GlobalPalette) => {
    setPalette(presetPalette);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Please enter a name for your brand package');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // Derive full component colors from palette
      const derivedColors = deriveComponentColors(palette);

      // Store both the palette (for editing) and derived colors (for rendering)
      // We store palette fields alongside component colors - API accepts any JSON
      const colorsToSave = {
        // Store palette at root for future editing
        _palette: palette,
        // Store derived component colors
        ...derivedColors,
      } as unknown as BrandPackageColors;

      let savedPackage: BrandPackage;

      if (isEditing && brandPackage) {
        savedPackage = await brandPackagesApi.update(brandPackage.id, {
          name: name.trim(),
          description: description.trim() || undefined,
          colors: colorsToSave,
          fonts,
        });
      } else {
        savedPackage = await brandPackagesApi.create({
          name: name.trim(),
          description: description.trim() || undefined,
          colors: colorsToSave,
          fonts,
        });
      }

      onSave(savedPackage);
    } catch (err) {
      console.error('Failed to save brand package:', err);
      setError('Failed to save brand package. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Get derived colors for preview
  const derivedColors = deriveComponentColors(palette);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onCancel}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Edit Brand Package' : 'Create Brand Package'}
            </h3>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-500">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex max-h-[70vh]">
            {/* Left side - Form */}
            <div className="flex-1 overflow-y-auto p-6 border-r border-gray-200">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Basic Info */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Basic Info</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="My Brand Package"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Description</label>
                    <textarea
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Optional description..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* Color Palette */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-900">Color Palette</h4>
                  <button
                    type="button"
                    onClick={() => setPalette(DEFAULT_PALETTE)}
                    className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Reset
                  </button>
                </div>

                {/* Preset Palettes */}
                <div className="mb-4">
                  <label className="block text-xs text-gray-500 mb-2">Quick Presets</label>
                  <div className="flex flex-wrap gap-2">
                    {PRESET_PALETTES.map(preset => (
                      <button
                        key={preset.name}
                        type="button"
                        onClick={() => applyPreset(preset.palette)}
                        className="flex items-center gap-1.5 px-2 py-1 text-xs border border-gray-200 rounded-md hover:bg-gray-50"
                      >
                        <div className="flex -space-x-1">
                          <div
                            className="w-3 h-3 rounded-full border border-white"
                            style={{ backgroundColor: preset.palette.primary }}
                          />
                          <div
                            className="w-3 h-3 rounded-full border border-white"
                            style={{ backgroundColor: preset.palette.dark }}
                          />
                        </div>
                        {preset.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Color Pickers */}
                <div className="grid grid-cols-2 gap-4">
                  {PALETTE_FIELDS.map(field => (
                    <div key={field.key} className="space-y-1">
                      <label className="block text-sm text-gray-700">{field.label}</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={palette[field.key]}
                          onChange={e => updatePaletteColor(field.key, e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                        />
                        <div className="flex-1">
                          <input
                            type="text"
                            value={palette[field.key]}
                            onChange={e => updatePaletteColor(field.key, e.target.value)}
                            className="w-full text-sm px-2 py-1 border border-gray-200 rounded font-mono"
                            placeholder="#000000"
                          />
                          <p className="text-xs text-gray-400 mt-0.5">{field.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fonts */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Typography</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Heading Font</label>
                    <select
                      value={fonts.headingFont}
                      onChange={e => {
                        loadGoogleFont(e.target.value);
                        setFonts(prev => ({ ...prev, headingFont: e.target.value }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{ fontFamily: `"${fonts.headingFont}", system-ui` }}
                    >
                      {AVAILABLE_FONTS.map(font => (
                        <option
                          key={font.name}
                          value={font.name}
                          style={{ fontFamily: `"${font.name}", system-ui` }}
                        >
                          {font.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Body Font</label>
                    <select
                      value={fonts.bodyFont}
                      onChange={e => {
                        loadGoogleFont(e.target.value);
                        setFonts(prev => ({ ...prev, bodyFont: e.target.value }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{ fontFamily: `"${fonts.bodyFont}", system-ui` }}
                    >
                      {AVAILABLE_FONTS.map(font => (
                        <option
                          key={font.name}
                          value={font.name}
                          style={{ fontFamily: `"${font.name}", system-ui` }}
                        >
                          {font.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Preview */}
            <div className="w-80 p-6 bg-gray-50 overflow-y-auto">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Live Preview</h4>
              <BrandPackagePreview
                colors={derivedColors}
                fonts={fonts}
                palette={palette}
                size="large"
              />

              {/* Font Preview */}
              <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                <div
                  className="text-lg font-semibold mb-1"
                  style={{
                    fontFamily: `"${fonts.headingFont}", system-ui`,
                    color: palette.text,
                  }}
                >
                  Heading Preview
                </div>
                <div
                  className="text-sm"
                  style={{
                    fontFamily: `"${fonts.bodyFont}", system-ui`,
                    color: palette.text,
                    opacity: 0.7,
                  }}
                >
                  This is how your body text will look with the selected font.
                </div>
              </div>

              {/* Color Swatch Preview */}
              <div className="mt-4">
                <p className="text-xs text-gray-500 mb-2">Your Palette</p>
                <div className="flex rounded-lg overflow-hidden border border-gray-200">
                  {PALETTE_FIELDS.map(field => (
                    <div
                      key={field.key}
                      className="flex-1 h-8"
                      style={{ backgroundColor: palette[field.key] }}
                      title={`${field.label}: ${palette[field.key]}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditing ? 'Save Changes' : 'Create Brand Package'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandPackageEditor;
